import { Dispatch, SetStateAction, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApplicationCreatedModal from "./ApplicationCreatedModal";
import ApplicationPendingModal from "./ApplicationPendingModal";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";

type PendingStage = "pending" | "selfManaged" | null;

type Guarantor = { name: string; email?: string };
type GuarantorWithStatus = Guarantor & { completed: boolean };

type Props = {
  createdApp: {
    title: string;
    peopleCount: number;
    pendingGuarantors?: Guarantor[];
  } | null;
  user: any;
  pendingStage: PendingStage;
  setPendingStage: Dispatch<SetStateAction<PendingStage>>;
  onClose: () => void;
};

function normalizeEmail(v: unknown): string {
  return typeof v === "string" ? v.toLowerCase() : "";
}

function normalizeName(v: unknown): string {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

function guarantorKey(g: Guarantor): string {
  return normalizeEmail(g.email) || normalizeName(g.name);
}

function isCompleted(
  g: Guarantor,
  added: any[],
  completedUsers: any[]
): boolean {
  const email = normalizeEmail(g.email);
  if (email) {
    if (completedUsers.some((c) => c?.type === "guarantor" && normalizeEmail(c?.email) === email)) {
      return true;
    }
    return added.some(
      (a) => normalizeEmail(a?.gurantor_email) === email || normalizeEmail(a?.guarantor_id?.email) === email
    );
  }

  const name = normalizeName(g.name);
  if (!name) return false;

  if (completedUsers.some((c) => c?.type === "guarantor" && normalizeName(c?.name) === name)) {
    return true;
  }

  return added.some((a) => {
    const addedName = normalizeName(`${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`);
    return !!addedName && addedName === name;
  });
}

function getPending(createdApp: Props["createdApp"], user: any): GuarantorWithStatus[] {
  const selected: Guarantor[] = (user?.tenant_extra_field_id?.selected_onboarding_users ?? [])
    .filter((u: any) => u?.type === "guarantor")
    .map((u: any) => ({ name: u.name, email: u.email }));

  const completedUsers: any[] = user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
  const completed: Guarantor[] = completedUsers
    .filter((u: any) => u?.type === "guarantor")
    .map((u: any) => ({ name: u.name, email: u.email }));

  const base: Guarantor[] = createdApp?.pendingGuarantors ?? [...selected, ...completed];

  const merged = new Map<string, Guarantor>();
  for (const g of base) {
    const key = guarantorKey(g);
    if (key && !merged.has(key)) merged.set(key, g);
  }
  if (createdApp?.pendingGuarantors) {
    for (const g of completed) {
      const key = guarantorKey(g);
      if (key && !merged.has(key)) merged.set(key, g);
    }
  }

  const raw: Guarantor[] = Array.from(merged.values());
  const added: any[] = user?.gurantor_added_id ?? [];

  return raw.map((g) => ({ ...g, completed: isCompleted(g, added, completedUsers) }));
}

export default function PostApplicationModals({
  createdApp,
  user,
  pendingStage,
  setPendingStage,
  onClose,
}: Props) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!createdApp) return;
    const pending = getPending(createdApp, user);
    if (pending.length > 0 && !pendingStage) setPendingStage("pending");
  }, [createdApp, user, pendingStage, setPendingStage]);

  if (!createdApp) return null;

  const pending = getPending(createdApp, user);
  const pendingCount = pending.filter((g) => !g.completed).length;
  const hasPending = pendingCount > 0;

  if (hasPending && pendingStage === "selfManaged") {
    return (
      <div
        className="modal-fixed-screen"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--skor-color-canvas, #f6f6f6)",
          padding: "24px 16px",
        }}
      >
        <SelfManagedGuarantorsModal
          guarantors={pending}
          onLater={() => {
            setPendingStage(null);
            onClose();
            navigate("/home?welcome=true");
          }}
          inline
        />
      </div>
    );
  }

  if (hasPending) {
    return (
      <ApplicationPendingModal
        title={createdApp.title}
        peopleCount={createdApp.peopleCount}
        pendingCount={pendingCount}
        onProceed={() => setPendingStage("selfManaged")}
        onLater={() => {
          setPendingStage(null);
          onClose();
          navigate("/home?welcome=true");
        }}
      />
    );
  }

  return (
    <ApplicationCreatedModal
      title={createdApp.title}
      peopleCount={createdApp.peopleCount}
      onClose={() => {
        setPendingStage(null);
        onClose();
      }}
    />
  );
}
